import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, ChevronDown, ChevronUp, Leaf, AlertTriangle, Lightbulb, Sunrise, Sun, Moon, Cookie, Coffee } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAYS_CONFIG = {
  lunes: { label: 'Lunes', short: 'L', color: 'from-blue-500 to-sky-400', ring: 'ring-blue-300', bg: 'bg-blue-50' },
  martes: { label: 'Martes', short: 'M', color: 'from-violet-500 to-purple-400', ring: 'ring-violet-300', bg: 'bg-violet-50' },
  miercoles: { label: 'Miercoles', short: 'X', color: 'from-emerald-500 to-teal-400', ring: 'ring-emerald-300', bg: 'bg-emerald-50' },
  jueves: { label: 'Jueves', short: 'J', color: 'from-amber-500 to-yellow-400', ring: 'ring-amber-300', bg: 'bg-amber-50' },
  viernes: { label: 'Viernes', short: 'V', color: 'from-red-500 to-rose-400', ring: 'ring-red-300', bg: 'bg-red-50' },
  sabado: { label: 'Sabado', short: 'S', color: 'from-pink-500 to-rose-400', ring: 'ring-pink-300', bg: 'bg-pink-50' },
  domingo: { label: 'Domingo', short: 'D', color: 'from-orange-500 to-amber-400', ring: 'ring-orange-300', bg: 'bg-orange-50' },
};

const MEAL_META = {
  desayuno: { label: 'Desayuno', icon: Sunrise, gradient: 'from-amber-500 to-orange-400' },
  merienda1: { label: 'Merienda 1', icon: Cookie, gradient: 'from-rose-500 to-pink-400' },
  almuerzo: { label: 'Almuerzo', icon: Sun, gradient: 'from-emerald-500 to-green-400' },
  merienda2: { label: 'Merienda 2', icon: Cookie, gradient: 'from-violet-500 to-purple-400' },
  cena: { label: 'Cena', icon: Moon, gradient: 'from-indigo-500 to-blue-400' },
};

const resolveImg = (url) => {
  if (!url) return null;
  return url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
};

const DistributorDietPreviewPage = () => {
  const { dietId } = useParams();
  const navigate = useNavigate();
  const [diet, setDiet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDayId, setSelectedDayId] = useState(null);
  const [openAccordion, setOpenAccordion] = useState(null);
  const [platosDb, setPlatosDb] = useState([]);
  const [bebidasDb, setBebidasDb] = useState([]);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/diet/distributor/custom/${dietId}`),
      axios.get(`${API}/diet/catalog/platos`).catch(() => ({ data: [] })),
      axios.get(`${API}/diet/catalog/bebidas`).catch(() => ({ data: [] })),
    ])
      .then(([dietRes, platosRes, bebidasRes]) => {
        setDiet(dietRes.data);
        setPlatosDb(platosRes.data || []);
        setBebidasDb(bebidasRes.data || []);
      })
      .catch(() => navigate('/distributor/profile/diets'))
      .finally(() => setLoading(false));
  }, [dietId, navigate]);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-[#467322]" size={32} /></div>;
  if (!diet) return null;

  const dietData = diet.days || diet.meals_data || {};
  const selectedDay = selectedDayId ? DAYS_CONFIG[selectedDayId] : null;
  const dayMeals = selectedDayId ? (dietData[selectedDayId] || {}) : {};

  const enrichPlato = (p) => {
    const full = platosDb.find(x => x.id === p.id);
    return full ? { ...p, ...full } : p;
  };

  const enrichBebida = (b) => {
    const full = bebidasDb.find(x => x.id === b.id);
    return full ? { ...b, ...full } : b;
  };

  return (
    <div className="max-w-6xl mx-auto pb-24" data-testid="dist-diet-preview">
      {/* Header */}
      <div className="flex items-center justify-between py-3 sticky top-14 bg-zinc-50/90 backdrop-blur-xl z-30">
        <button onClick={() => selectedDayId ? setSelectedDayId(null) : navigate('/distributor/profile/diets')}
          className="flex items-center gap-2 text-[#467322] font-semibold text-sm hover:text-[#1A2E10] transition-colors"
          data-testid="back-btn">
          <ArrowLeft size={18} /> {selectedDayId ? 'Vista Semanal' : 'Mis Dietas'}
        </button>
        <h2 className="text-sm font-bold text-gray-500" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          {diet.name}
        </h2>
      </div>

      {!selectedDayId ? (
        /* ========== WEEKLY GRID ========== */
        <>
          <h1 className="text-2xl font-black text-[#1A2E10] mb-1 mt-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Preview: {diet.name}
          </h1>
          <p className="text-sm text-gray-500 mb-6">{diet.selected_days?.length || 0} dias, {diet.selected_meals?.length || 0} comidas/dia</p>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3">
            {(diet.selected_days || []).map(dayId => {
              const dayConfig = DAYS_CONFIG[dayId];
              const meals = dietData[dayId] || {};
              const mealCount = Object.keys(meals).filter(k => {
                if (k === 'bebidas') return (meals[k] || []).length > 0;
                return (meals[k]?.platos || []).length > 0;
              }).length;
              const firstPlato = Object.values(meals).find(m => m?.platos?.length > 0)?.platos?.[0];
              const previewImg = resolveImg(firstPlato?.image_url);

              return (
                <button key={dayId} onClick={() => setSelectedDayId(dayId)}
                  className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all group"
                  data-testid={`day-${dayId}`}>
                  <div className={`h-28 bg-gradient-to-br ${dayConfig?.color} relative overflow-hidden`}>
                    {previewImg && (
                      <img src={previewImg} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" onError={e => { e.target.style.display = 'none'; }} />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-white text-3xl font-black drop-shadow-lg" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                        {dayConfig?.short}
                      </span>
                    </div>
                  </div>
                  <div className="p-3 text-center">
                    <p className="text-sm font-bold text-gray-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{dayConfig?.label}</p>
                    <p className="text-[10px] text-gray-400 mt-0.5">{mealCount} comida{mealCount !== 1 ? 's' : ''}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </>
      ) : (
        /* ========== DAY DETAIL ========== */
        <div className="mt-2">
          <div className={`bg-gradient-to-r ${selectedDay?.color || 'from-gray-500 to-gray-600'} rounded-2xl p-5 mb-6 shadow-lg`}>
            <p className="text-white/70 text-xs font-bold uppercase tracking-wider">Menu del dia</p>
            <p className="text-white text-3xl font-black" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{selectedDay?.label}</p>
          </div>

          <div className="space-y-6">
            {(diet.selected_meals || []).map(mealId => {
              const meta = MEAL_META[mealId];
              if (!meta) return null;
              const mealData = dayMeals[mealId] || {};
              const mealPlatos = (mealData.platos || []).map(enrichPlato);
              const IconComp = meta.icon;

              return (
                <div key={mealId} data-testid={`meal-section-${mealId}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${meta.gradient} flex items-center justify-center shadow-lg`}>
                      <IconComp size={18} className="text-white" />
                    </div>
                    <div>
                      <p className="font-black text-xl text-[#1A2E10]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{meta.label}</p>
                      <p className="text-xs text-gray-400">{mealPlatos.length} plato{mealPlatos.length !== 1 ? 's' : ''}</p>
                    </div>
                  </div>

                  {mealPlatos.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {mealPlatos.map((plato, i) => {
                        const imgUrl = resolveImg(plato.image_url);
                        return (
                          <div key={plato.id || i} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                            <div className="h-44 sm:h-52 bg-gray-100 overflow-hidden relative">
                              {imgUrl ? (
                                <img src={imgUrl} alt={plato.nombre} className="w-full h-full object-cover" onError={e => { e.target.style.display = 'none'; }} />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-300 bg-gradient-to-br from-gray-100 to-gray-200">
                                  <Sun size={40} />
                                </div>
                              )}
                            </div>
                            <div className="p-4 space-y-3">
                              <p className="font-black text-lg text-[#1A2E10] leading-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{plato.nombre}</p>
                              {plato.descripcion && <p className="text-sm text-gray-600 leading-relaxed">{plato.descripcion}</p>}
                              {plato.ingredientes && (
                                <div>
                                  <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1.5">Ingredientes</p>
                                  <div className="flex flex-wrap gap-1.5">
                                    {plato.ingredientes.split(',').map((ing) => (
                                      <span key={ing.trim()} className="text-[11px] bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-lg border border-emerald-100">{ing.trim()}</span>
                                    ))}
                                  </div>
                                </div>
                              )}
                              <div className="flex gap-2 flex-wrap">
                                {plato.tipo_comida && <span className="text-[10px] font-bold bg-amber-50 text-amber-700 px-2 py-0.5 rounded-lg border border-amber-200">{plato.tipo_comida}</span>}
                                {plato.etiquetas && <span className="text-[10px] font-bold bg-violet-50 text-violet-700 px-2 py-0.5 rounded-lg border border-violet-200">{plato.etiquetas}</span>}
                                {plato.calorias !== undefined && <span className="text-[10px] font-bold bg-gray-100 text-gray-600 px-2 py-0.5 rounded-lg">{plato.calorias} kcal</span>}
                              </div>
                              {plato.receta && (
                                <div>
                                  <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1.5">Receta</p>
                                  <div className="bg-gray-50 rounded-xl p-3 space-y-2">
                                    {plato.receta.split(/Paso \d+:/).filter(Boolean).map((paso, pi) => (
                                      <div key={`paso-${pi + 1}`} className="flex gap-2">
                                        <span className="w-5 h-5 rounded-full bg-[#467322] text-white text-[10px] flex items-center justify-center flex-shrink-0 font-bold">{pi + 1}</span>
                                        <p className="text-xs text-gray-700 leading-relaxed mt-0.5">{paso.trim()}</p>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="bg-gray-50 rounded-xl p-6 text-center text-gray-400 text-sm">Sin platos asignados</div>
                  )}
                </div>
              );
            })}

            {/* Bebidas del dia */}
            {(dayMeals.bebidas || []).length > 0 && (
              <div data-testid="meal-section-bebidas">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center shadow-lg">
                    <Coffee size={18} className="text-white" />
                  </div>
                  <div>
                    <p className="font-black text-xl text-[#1A2E10]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Bebidas del Dia</p>
                    <p className="text-xs text-gray-400">{dayMeals.bebidas.length} bebida{dayMeals.bebidas.length !== 1 ? 's' : ''}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {dayMeals.bebidas.map(enrichBebida).map((b, i) => {
                    const imgUrl = resolveImg(b.image_url);
                    return (
                      <div key={b.id || i} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                        <div className="h-20 bg-gray-100 overflow-hidden">
                          {imgUrl && <img src={imgUrl} alt={b.nombre} className="w-full h-full object-cover" onError={e => { e.target.style.display = 'none'; }} />}
                        </div>
                        <div className="p-2.5">
                          <p className="font-bold text-xs text-gray-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{b.nombre}</p>
                          {b.calorias !== undefined && <p className="text-[10px] text-gray-400 mt-0.5">{b.calorias} kcal</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Info accordions */}
      {!selectedDayId && (
        <div className="mt-8 space-y-3">
          {[
            { id: 'vegetales', title: 'Vegetales Permitidos', icon: Leaf, color: 'emerald',
              content: ['Brocoli y coliflor', 'Rabano', 'Apio', 'Pepino', 'Zanahoria', 'Repollo y berro', 'Tomate'] },
            { id: 'prohibidos', title: 'Alimentos Prohibidos', icon: AlertTriangle, color: 'red',
              content: ['Azucar refinada', 'Harinas blancas', 'Refrescos', 'Alcohol', 'Embutidos procesados', 'Frituras'] },
            { id: 'tips', title: 'Tips Nutricionales', icon: Lightbulb, color: 'blue',
              content: ['Beber 8 vasos de agua al dia', 'Comer cada 3 horas', 'No saltarse el desayuno', 'Cenar ligero antes de las 7pm'] },
          ].map(section => (
            <div key={section.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <button onClick={() => setOpenAccordion(openAccordion === section.id ? null : section.id)}
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50/50 transition-colors"
                data-testid={`accordion-${section.id}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-xl bg-${section.color}-100 flex items-center justify-center`}>
                    <section.icon size={18} className={`text-${section.color}-600`} />
                  </div>
                  <span className="font-bold text-sm text-gray-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{section.title}</span>
                </div>
                {openAccordion === section.id ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
              </button>
              {openAccordion === section.id && (
                <div className="px-4 pb-4 animate-fade-in">
                  <div className="space-y-2">
                    {section.content.map(item => (
                      <div key={item} className={`flex items-center gap-2.5 bg-${section.color}-50/60 rounded-xl border border-${section.color}-100/60 px-3.5 py-3`}>
                        <div className={`w-2 h-2 rounded-full bg-${section.color}-400`} />
                        <span className="text-sm text-gray-700">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DistributorDietPreviewPage;
